import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  CheckCircle, 
  AlertCircle, 
  Info, 
  Zap, 
  Gift, 
  TrendingUp,
  Settings,
  Trash2,
  Search,
  ExternalLink,
  Calendar
} from 'lucide-react';

interface Notification {
  id: number;
  type: 'transaction' | 'staking' | 'reward' | 'system' | 'security' | 'bridge' | 'boom_event';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  action_url?: string;
  metadata?: {
    amount?: number;
    token?: string;
    tx_hash?: string;
    event_id?: number;
  };
}

interface NotificationSettings {
  email_enabled: boolean;
  push_enabled: boolean;
  transaction_notifications: boolean;
  staking_notifications: boolean;
  reward_notifications: boolean;
  system_notifications: boolean;
  security_notifications: boolean;
  bridge_notifications: boolean;
  boom_event_notifications: boolean;
}

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('notifications');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [walletAddress, setWalletAddress] = useState('');

  useEffect(() => {
    checkWalletConnection();
  }, []);

  const checkWalletConnection = () => {
    const savedAddress = localStorage.getItem('walletAddress');
    if (savedAddress) {
      setWalletAddress(savedAddress);
      fetchNotifications(savedAddress);
      fetchSettings(savedAddress);
    } else {
      setLoading(false);
    }
  };

  const fetchNotifications = async (address: string) => {
    try {
      setLoading(true);
      const response = await fetch(`http://localhost:3600/api/notifications/${address}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setNotifications(data.data);
        }
      }
    } catch (error) {
      console.error('알림 가져오기 실패:', error);
      // 더미 데이터
      setNotifications([
        {
          id: 1,
          type: 'transaction',
          title: '거래 완료',
          message: '100 VTTN이 성공적으로 전송되었습니다.',
          timestamp: '2024-01-15T10:30:00Z',
          read: false,
          priority: 'medium',
          metadata: {
            amount: 100,
            token: 'VTTN',
            tx_hash: '0xabc123...'
          }
        },
        {
          id: 2,
          type: 'reward',
          title: '스테이킹 보상 지급',
          message: '일일 스테이킹 보상 25.5 VTTN이 지급되었습니다.',
          timestamp: '2024-01-15T09:00:00Z',
          read: false,
          priority: 'low',
          metadata: {
            amount: 25.5,
            token: 'VTTN'
          }
        },
        {
          id: 3,
          type: 'boom_event',
          title: '새로운 BOOM 이벤트',
          message: '볼트론 네트워크 런칭 기념 에어드랍 이벤트가 시작되었습니다!',
          timestamp: '2024-01-15T08:00:00Z',
          read: true,
          priority: 'high',
          action_url: '/boom-events',
          metadata: {
            event_id: 1
          }
        },
        {
          id: 4,
          type: 'security',
          title: '보안 알림',
          message: '새로운 기기에서 로그인이 감지되었습니다.',
          timestamp: '2024-01-14T20:15:00Z',
          read: true,
          priority: 'urgent'
        },
        {
          id: 5,
          type: 'bridge',
          title: '브릿지 완료',
          message: '이더리움에서 볼트론으로 500 USDT 브릿지가 완료되었습니다.',
          timestamp: '2024-01-14T15:30:00Z',
          read: true,
          priority: 'medium',
          metadata: {
            amount: 500,
            token: 'USDT'
          }
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async (address: string) => {
    try {
      const response = await fetch(`http://localhost:3600/api/notifications/settings/${address}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSettings(data.data);
        }
      }
    } catch (error) {
      console.error('알림 설정 가져오기 실패:', error);
      // 기본 설정
      setSettings({
        email_enabled: true,
        push_enabled: true,
        transaction_notifications: true,
        staking_notifications: true,
        reward_notifications: true,
        system_notifications: true,
        security_notifications: true,
        bridge_notifications: true,
        boom_event_notifications: true
      });
    }
  };

  const markAsRead = async (notificationId: number) => {
    try {
      const response = await fetch(`http://localhost:3600/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ wallet_address: walletAddress })
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === notificationId ? { ...notif, read: true } : notif
          )
        );
      }
    } catch (error) {
      console.error('알림 읽음 처리 실패:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await fetch(`http://localhost:3600/api/notifications/read-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ wallet_address: walletAddress })
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(notif => ({ ...notif, read: true }))
        );
      }
    } catch (error) {
      console.error('모든 알림 읽음 처리 실패:', error);
    }
  };

  const deleteNotification = async (notificationId: number) => {
    try {
      const response = await fetch(`http://localhost:3600/api/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ wallet_address: walletAddress })
      });

      if (response.ok) {
        setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      }
    } catch (error) {
      console.error('알림 삭제 실패:', error);
    }
  };

  const updateSettings = async (newSettings: NotificationSettings) => {
    try {
      const response = await fetch(`http://localhost:3600/api/notifications/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          wallet_address: walletAddress,
          settings: newSettings
        })
      });

      if (response.ok) {
        setSettings(newSettings);
        alert('설정이 저장되었습니다.');
      }
    } catch (error) {
      console.error('설정 업데이트 실패:', error);
      alert('설정 저장에 실패했습니다.');
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'transaction': return <Zap className="w-5 h-5 text-blue-500" />;
      case 'staking': return <TrendingUp className="w-5 h-5 text-green-500" />;
      case 'reward': return <Gift className="w-5 h-5 text-purple-500" />;
      case 'system': return <Info className="w-5 h-5 text-gray-500" />;
      case 'security': return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'bridge': return <ExternalLink className="w-5 h-5 text-orange-500" />;
      case 'boom_event': return <Bell className="w-5 h-5 text-yellow-500" />;
      default: return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'border-l-red-500 bg-red-50';
      case 'high': return 'border-l-orange-500 bg-orange-50';
      case 'medium': return 'border-l-blue-500 bg-blue-50';
      case 'low': return 'border-l-gray-500 bg-gray-50';
      default: return 'border-l-gray-300 bg-white';
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'transaction': return '거래';
      case 'staking': return '스테이킹';
      case 'reward': return '보상';
      case 'system': return '시스템';
      case 'security': return '보안';
      case 'bridge': return '브릿지';
      case 'boom_event': return 'BOOM 이벤트';
      default: return type;
    }
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filter !== 'all' && notif.type !== filter) return false;
    if (searchTerm && !notif.title.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !notif.message.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const unreadCount = notifications.filter(notif => !notif.read).length;

  if (!walletAddress) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="text-center">
            <Bell className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">알림 센터</h2>
            <p className="text-gray-600 mb-6">
              지갑을 연결하여 알림을 확인하세요.
            </p>
            <button
              onClick={() => window.location.href = '/wallet'}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              지갑 연결하기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
              <Bell className="w-8 h-8 text-orange-500" />
              <span>알림 센터</span>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  {unreadCount}
                </span>
              )}
            </h1>
            <p className="text-gray-600 mt-1">
              Voltron 메인넷의 모든 알림을 한 곳에서 관리하세요
            </p>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm transition-colors"
            >
              <CheckCircle className="w-4 h-4" />
              <span>모두 읽음</span>
            </button>
          )}
        </div>
      </div>

      {/* 탭 메뉴 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'notifications', name: '알림', icon: Bell },
              { id: 'settings', name: '설정', icon: Settings }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              {/* 필터 및 검색 */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="알림 검색..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="all">모든 알림</option>
                  <option value="transaction">거래</option>
                  <option value="staking">스테이킹</option>
                  <option value="reward">보상</option>
                  <option value="system">시스템</option>
                  <option value="security">보안</option>
                  <option value="bridge">브릿지</option>
                  <option value="boom_event">BOOM 이벤트</option>
                </select>
              </div>

              {/* 알림 목록 */}
              <div className="space-y-3">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
                    <p className="text-gray-600 mt-2">알림을 불러오는 중...</p>
                  </div>
                ) : filteredNotifications.length > 0 ? (
                  filteredNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`border-l-4 rounded-lg p-4 transition-all duration-200 ${
                        notification.read ? 'bg-white' : getPriorityColor(notification.priority)
                      } ${!notification.read ? 'shadow-sm' : ''}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          {getNotificationIcon(notification.type)}
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className={`font-medium ${
                                notification.read ? 'text-gray-700' : 'text-gray-900'
                              }`}>
                                {notification.title}
                              </h4>
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                                {getTypeText(notification.type)}
                              </span>
                              {!notification.read && (
                                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                              )}
                            </div>
                            <p className={`text-sm mb-2 ${
                              notification.read ? 'text-gray-500' : 'text-gray-700'
                            }`}>
                              {notification.message}
                            </p>
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span className="flex items-center space-x-1">
                                <Calendar className="w-3 h-3" />
                                <span>{new Date(notification.timestamp).toLocaleString()}</span>
                              </span>
                              {notification.metadata?.amount && (
                                <span>
                                  {notification.metadata.amount} {notification.metadata.token}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {notification.action_url && (
                            <button
                              onClick={() => window.location.href = notification.action_url!}
                              className="p-1 text-orange-500 hover:text-orange-700 transition-colors"
                              title="상세 보기"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </button>
                          )}
                          {!notification.read && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="p-1 text-green-500 hover:text-green-700 transition-colors"
                              title="읽음 처리"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => deleteNotification(notification.id)}
                            className="p-1 text-red-500 hover:text-red-700 transition-colors"
                            title="삭제"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">알림이 없습니다.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'settings' && settings && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">알림 설정</h3>
              
              {/* 전체 설정 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">전체 설정</h4>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.email_enabled}
                      onChange={(e) => setSettings({ ...settings, email_enabled: e.target.checked })}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700">이메일 알림 활성화</span>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.push_enabled}
                      onChange={(e) => setSettings({ ...settings, push_enabled: e.target.checked })}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700">푸시 알림 활성화</span>
                  </label>
                </div>
              </div>

              {/* 카테고리별 설정 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">카테고리별 알림</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { key: 'transaction_notifications', label: '거래 알림', icon: Zap },
                    { key: 'staking_notifications', label: '스테이킹 알림', icon: TrendingUp },
                    { key: 'reward_notifications', label: '보상 알림', icon: Gift },
                    { key: 'system_notifications', label: '시스템 알림', icon: Info },
                    { key: 'security_notifications', label: '보안 알림', icon: AlertCircle },
                    { key: 'bridge_notifications', label: '브릿지 알림', icon: ExternalLink },
                    { key: 'boom_event_notifications', label: 'BOOM 이벤트 알림', icon: Bell }
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <label key={item.key} className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={settings[item.key as keyof NotificationSettings] as boolean}
                          onChange={(e) => setSettings({ 
                            ...settings, 
                            [item.key]: e.target.checked 
                          })}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <Icon className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">{item.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* 저장 버튼 */}
              <div className="flex justify-end">
                <button
                  onClick={() => updateSettings(settings)}
                  className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
                >
                  설정 저장
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;